import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Trash2,
  ExternalLink,
  Receipt as ReceiptIcon,
  Wallet,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/expenses/$expenseId")({
  component: ExpenseDetail,
});

type ExpenseStatus = "pending" | "approved" | "rejected" | "reimbursed";

type Expense = {
  id: string;
  category: string;
  vendor: string | null;
  description: string | null;
  amount: number;
  currency: string;
  expense_date: string;
  payment_method: string | null;
  reimbursable: boolean;
  status: ExpenseStatus;
  receipt_path: string | null;
  rejection_reason: string | null;
  notes: string | null;
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  project_id: string | null;
  job_site_id: string | null;
  projects: { id: string; name: string; code: string } | null;
  job_sites: { id: string; name: string } | null;
};

const STATUS_VARIANT: Record<ExpenseStatus, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  approved: "default",
  rejected: "destructive",
  reimbursed: "outline",
};

function formatMoney(n: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

function ExpenseDetail() {
  const { t } = useTranslation();
  const { expenseId } = Route.useParams();
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuth();
  const canApprove = hasAnyRole(["admin", "supervisor"]);
  const canReimburse = hasAnyRole(["admin"]);

  const [expense, setExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [editForm, setEditForm] = useState({
    vendor: "",
    description: "",
    notes: "",
    project_id: "" as string,
  });

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("expenses")
      .select(
        "id, category, vendor, description, amount, currency, expense_date, payment_method, reimbursable, status, receipt_path, rejection_reason, notes, created_by, approved_by, approved_at, created_at, project_id, job_site_id, projects(id, name, code), job_sites(id, name)",
      )
      .eq("id", expenseId)
      .maybeSingle();
    if (error) toast.error(error.message);
    setExpense((data ?? null) as Expense | null);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!expense?.receipt_path) {
        setReceiptUrl(null);
        return;
      }
      const { data } = await supabase.storage
        .from("receipts")
        .createSignedUrl(expense.receipt_path, 60 * 10);
      if (!cancelled) setReceiptUrl(data?.signedUrl ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [expense?.receipt_path]);

  // Load projects list once, for the edit form's project selector.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("projects")
        .select("id, name, code")
        .order("created_at", { ascending: false });
      setProjects((data ?? []) as Array<{ id: string; name: string; code: string }>);
    })();
  }, []);

  function startEdit() {
    if (!expense) return;
    setEditForm({
      vendor: expense.vendor ?? "",
      description: expense.description ?? "",
      notes: expense.notes ?? "",
      project_id: expense.project_id ?? "",
    });
    setEditing(true);
  }

  async function saveEdit() {
    if (!expense) return;
    setWorking(true);
    const { error } = await supabase
      .from("expenses")
      .update({
        vendor: editForm.vendor.trim() || null,
        description: editForm.description.trim() || null,
        notes: editForm.notes.trim() || null,
        project_id: editForm.project_id || null,
      })
      .eq("id", expense.id);
    setWorking(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("expenseDetail.toasts.updated"));
    setEditing(false);
    load();
  }

  async function setStatus(status: ExpenseStatus, reason?: string) {
    if (!expense) return;
    setWorking(true);
    const stamp = status === "approved" || status === "rejected";
    const { error } = await supabase
      .from("expenses")
      .update({
        status,
        rejection_reason: status === "rejected" ? (reason ?? null) : null,
        approved_by: stamp ? (user?.id ?? null) : null,
        approved_at: stamp ? new Date().toISOString() : null,
      })
      .eq("id", expense.id);
    setWorking(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(
      t("expenseDetail.toasts.marked", { status: t(`expenseDetail.statuses.${status}`) }),
    );
    load();
  }

  async function handleDelete() {
    if (!expense) return;
    setWorking(true);
    if (expense.receipt_path) {
      await supabase.storage.from("receipts").remove([expense.receipt_path]);
    }
    const { error } = await supabase.from("expenses").delete().eq("id", expense.id);
    setWorking(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("expenseDetail.toasts.deleted"));
    navigate({ to: "/expenses" });
  }

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground">{t("expenseDetail.loading")}</div>;
  }
  if (!expense) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/expenses">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("expenseDetail.back")}
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">{t("expenseDetail.notFound")}</p>
      </div>
    );
  }

  const isOwner = expense.created_by === user?.id;
  const canDelete = (isOwner && expense.status === "pending") || canReimburse;
  const canEdit = (isOwner && expense.status === "pending") || canReimburse;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm">
          <Link to="/expenses">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("expenseDetail.back")}
          </Link>
        </Button>
        <div className="flex items-center gap-1">
          {canEdit && !editing && (
            <Button variant="ghost" size="sm" onClick={startEdit}>
              <Pencil className="mr-1 h-4 w-4" />
              {t("expenseDetail.edit")}
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive">
                  <Trash2 className="mr-1 h-4 w-4" />
                  {t("expenseDetail.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("expenseDetail.deleteTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("expenseDetail.deleteDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("expenseDetail.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} disabled={working}>
                    {t("expenseDetail.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {expense.vendor || expense.description || t("expenseDetail.expense")}
            </h1>
            <Badge variant={STATUS_VARIANT[expense.status]}>
              {t(`expenseDetail.statuses.${expense.status}`)}
            </Badge>
            {expense.reimbursable && (
              <Badge variant="outline">{t("expenseDetail.reimbursable")}</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {new Date(expense.expense_date).toLocaleDateString()} •{" "}
            <span className="capitalize">{expense.category}</span>
            {expense.payment_method ? ` • ${expense.payment_method.replace("_", " ")}` : ""}
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold tabular-nums">
            {formatMoney(Number(expense.amount), expense.currency)}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("expenseDetail.details")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
            {editing ? (
              <>
                <div className="sm:col-span-2">
                  <Label htmlFor="edit-vendor" className="text-xs">
                    {t("expenseDetail.vendor")}
                  </Label>
                  <Input
                    id="edit-vendor"
                    value={editForm.vendor}
                    onChange={(e) => setEditForm((f) => ({ ...f, vendor: e.target.value }))}
                    placeholder={t("expenseDetail.vendorPlaceholder")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="edit-project" className="text-xs">
                    {t("expenseDetail.project")}
                  </Label>
                  <Select
                    value={editForm.project_id || "none"}
                    onValueChange={(v) =>
                      setEditForm((f) => ({ ...f, project_id: v === "none" ? "" : v }))
                    }
                  >
                    <SelectTrigger id="edit-project">
                      <SelectValue placeholder={t("expenseDetail.noProject")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("expenseDetail.noProject")}</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.code} — {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="edit-description" className="text-xs">
                    {t("expenseDetail.description")}
                  </Label>
                  <Input
                    id="edit-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder={t("expenseDetail.descriptionPlaceholder")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="edit-notes" className="text-xs">
                    {t("expenseDetail.notes")}
                  </Label>
                  <Textarea
                    id="edit-notes"
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder={t("expenseDetail.notesPlaceholder")}
                  />
                </div>
                <div className="sm:col-span-2 flex gap-2">
                  <Button onClick={saveEdit} disabled={working}>
                    {t("expenseDetail.save")}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)} disabled={working}>
                    {t("expenseDetail.cancel")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("expenseDetail.project")}
                  </p>
                  {expense.projects ? (
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: expense.projects.id }}
                      className="font-medium text-primary hover:underline"
                    >
                      {expense.projects.code} — {expense.projects.name}
                    </Link>
                  ) : (
                    <p className="text-muted-foreground">—</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("expenseDetail.jobSite")}
                  </p>
                  {expense.job_sites ? (
                    <Link
                      to="/job-sites/$jobSiteId"
                      params={{ jobSiteId: expense.job_sites.id }}
                      className="font-medium text-primary hover:underline"
                    >
                      {expense.job_sites.name}
                    </Link>
                  ) : (
                    <p className="text-muted-foreground">—</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("expenseDetail.description")}
                  </p>
                  <p>{expense.description || "—"}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("expenseDetail.notes")}
                  </p>
                  <p className="whitespace-pre-wrap">{expense.notes || "—"}</p>
                </div>
                {expense.status === "rejected" && expense.rejection_reason && (
                  <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-xs uppercase tracking-wider text-destructive">
                      {t("expenseDetail.rejectionReason")}
                    </p>
                    <p className="text-sm">{expense.rejection_reason}</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ReceiptIcon className="h-4 w-4" /> {t("expenseDetail.receipt")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {receiptUrl ? (
                <Button asChild variant="outline" className="w-full">
                  <a href={receiptUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("expenseDetail.viewReceipt")}
                  </a>
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">{t("expenseDetail.noReceipt")}</p>
              )}
            </CardContent>
          </Card>

          {(canApprove || canReimburse) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="h-4 w-4" /> {t("expenseDetail.workflow")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {canApprove && expense.status === "pending" && (
                  <>
                    <Button
                      className="w-full"
                      onClick={() => setStatus("approved")}
                      disabled={working}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      {t("expenseDetail.approve")}
                    </Button>
                    <div className="space-y-2">
                      <Label htmlFor="reason" className="text-xs">
                        {t("expenseDetail.rejectionReasonOptional")}
                      </Label>
                      <Textarea
                        id="reason"
                        rows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setStatus("rejected", rejectReason)}
                        disabled={working}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        {t("expenseDetail.reject")}
                      </Button>
                    </div>
                  </>
                )}
                {canReimburse && expense.reimbursable && expense.status === "approved" && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => setStatus("reimbursed")}
                    disabled={working}
                  >
                    {t("expenseDetail.markReimbursed")}
                  </Button>
                )}
                {canApprove && expense.status !== "pending" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setStatus("pending")}
                    disabled={working}
                  >
                    {t("expenseDetail.resetPending")}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
